<template>
  <div class="exam-upload">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>上传试卷</h2>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-refresh" @click="loadExams">刷新</el-button>
      </div>
    </div>

    <!-- 考试列表 -->
    <div class="exam-list" v-loading="loading">
      <div v-if="exams.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <p>无考试信息</p>
      </div>

      <el-table v-else :data="exams" border stripe style="width: 100%">
        <el-table-column prop="examName" label="考试名称" min-width="200">
          <template slot-scope="scope">
            <div class="exam-name-cell">
              <div class="exam-name-row">
                <span class="exam-name" @click="openExamUpload(scope.row)">{{ scope.row.examName }}</span>
                <span class="exam-id">ID: {{ scope.row.id }}</span>
              </div>
              <div class="active-paper-info" v-if="scope.row.activePaperName">
                <i class="el-icon-document"></i>
                <span class="paper-name">使用试卷：{{ scope.row.activePaperName }}</span>
                <el-tag size="mini" type="success">{{ scope.row.activePaperCode }}卷</el-tag>
              </div>
              <div class="no-active-paper" v-else>
                <i class="el-icon-warning"></i>
                <span class="no-paper-text">暂无使用试卷</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="examDescription" label="考试描述" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <div class="exam-description">
              {{ scope.row.examDescription || '暂无描述' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="examDuration" label="时长" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="small" type="info">
              {{ scope.row.examDuration }} 分钟
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="paperCount" label="试卷套数" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="small" type="success">
              {{ scope.row.paperCount || 1 }} 套
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="totalStudents" label="参与人数" width="120" align="center">
          <template slot-scope="scope">
            <div class="student-count">
              <i class="el-icon-user"></i>
              {{ scope.row.totalStudents || 0 }} 人
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="uploadedCount" label="已上传" width="100" align="center">
          <template slot-scope="scope">
            <div class="uploaded-count">
              <i class="el-icon-upload"></i>
              {{ scope.row.uploadedCount || 0 }}
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="startTime" label="开始时间" width="160" align="center">
          <template slot-scope="scope">
            <div class="time-info">
              <div class="start-time">{{ formatTime(scope.row.startTime) }}</div>
              <div class="end-time" v-if="scope.row.endTime">
                至 {{ formatTime(scope.row.endTime) }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusTagType(scope.row.status)" size="small">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="操作" min-width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="operation-buttons">
              <el-button
                type="primary"
                size="mini"
                icon="el-icon-upload"
                @click="openExamUpload(scope.row)"
                title="单人上传试卷"
              >
                单人上传
              </el-button>
              <el-button
                type="success"
                size="mini"
                icon="el-icon-view"
                @click="viewExamDetails(scope.row)"
                title="查看详情"
              >
                详情
              </el-button>
              <el-button
                type="warning"
                size="mini"
                icon="el-icon-upload2"
                @click="showBatchUploadDialog(scope.row)"
                title="批量上传试卷"
              >
                批量上传
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 试卷上传对话框 -->
    <el-dialog
      title="上传试卷"
      :visible.sync="uploadDialogVisible"
      width="90%"
      :before-close="closeUploadDialog"
    >
      <div class="upload-container">
        <div class="upload-header">
          <h3>{{ currentExam.examName }}</h3>
          <div class="upload-actions">
            <el-button type="success" @click="refreshStudentList">刷新列表</el-button>
          </div>
        </div>

        <!-- 学生列表 -->
        <div class="student-list">
          <el-table :data="studentList" border v-loading="studentLoading">
            <el-table-column type="index" label="序号" width="60"></el-table-column>
            <el-table-column prop="studentNumber" label="学号" width="120"></el-table-column>
            <el-table-column prop="studentName" label="姓名" width="100">
              <template slot-scope="scope">
                <span :class="{ 'missing-paper': !scope.row.hasUploaded }">
                  {{ scope.row.studentName }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="className" label="班级" width="120"></el-table-column>
            <el-table-column prop="paperCode" label="试卷代码" width="100"></el-table-column>
            <el-table-column label="试卷状态" width="120">
              <template slot-scope="scope">
                <el-tag :type="getPaperStatusType(scope.row.paperStatus)">
                  {{ getPaperStatusText(scope.row.paperStatus) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="AI识别状态" width="120">
              <template slot-scope="scope">
                <el-tag :type="getAIRecognitionType(scope.row.aiRecognitionStatus)">
                  {{ getAIRecognitionText(scope.row.aiRecognitionStatus) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="aiConfidence" label="识别置信度" width="120">
              <template slot-scope="scope">
                <span v-if="scope.row.aiConfidence">
                  {{ (scope.row.aiConfidence * 100).toFixed(1) }}%
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="uploadTime" label="上传时间" width="160">
              <template slot-scope="scope">
                {{ formatTime(scope.row.uploadTime) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="200">
              <template slot-scope="scope">
                <el-button
                  type="primary"
                  size="mini"
                  @click="uploadSingleStudent(scope.row)"
                  v-if="!scope.row.hasUploaded"
                >
                  上传试卷
                </el-button>
                <el-button
                  type="success"
                  size="mini"
                  @click="viewPaper(scope.row)"
                  v-if="scope.row.hasUploaded && !disablePaperPreview"
                >
                  查看试卷
                </el-button>
                <el-button
                  type="warning"
                  size="mini"
                  @click="reuploadPaper(scope.row)"
                  v-if="scope.row.hasUploaded"
                >
                  重新上传
                </el-button>
                <el-button
                  type="info"
                  size="mini"
                  @click="verifyRecognition(scope.row)"
                  v-if="scope.row.aiRecognitionStatus === 1"
                >
                  验证识别
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="uploadDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 单个学生试卷上传对话框 -->
    <el-dialog
      title="上传试卷"
      :visible.sync="singleUploadDialogVisible"
      width="600px"
    >
      <div class="single-upload">
        <div class="student-info">
          <h4>学生信息</h4>
          <p><strong>学号：</strong>{{ currentStudent.studentNumber }}</p>
          <p><strong>姓名：</strong>{{ currentStudent.studentName }}</p>
          <p><strong>班级：</strong>{{ currentStudent.className }}</p>
          <p><strong>试卷代码：</strong>{{ currentStudent.paperCode }}</p>
        </div>

        <div class="upload-area">
          <el-upload
            ref="upload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :data="uploadData"
            :before-upload="beforeUpload"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :file-list="fileList"
            :auto-upload="false"
            :limit="10"
            multiple
            accept="image/*"
            drag
            name="files"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div class="el-upload__tip" slot="tip">
              支持上传多张jpg/png图片，每张不超过10MB，最多10张
            </div>
          </el-upload>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="singleUploadDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitUpload">确定上传</el-button>
      </div>
    </el-dialog>

    <!-- 批量上传对话框 -->
    <el-dialog
      :title="`批量上传试卷 - ${currentExam.examName || '未知考试'}`"
      :visible.sync="batchUploadDialogVisible"
      width="800px"
    >
      <div class="batch-upload">
        <!-- 考试信息 -->
        <div class="exam-info" v-if="currentExam.id">
          <h4>考试信息</h4>
          <div class="exam-details">
            <div class="detail-row">
              <span class="label">考试名称：</span>
              <span class="value">{{ currentExam.examName }}</span>
            </div>
            <div class="detail-row">
              <span class="label">参与人数：</span>
              <span class="value">{{ currentExam.totalStudents || 0 }} 人</span>
            </div>
            <div class="detail-row">
              <span class="label">已上传：</span>
              <span class="value">{{ currentExam.uploadedCount || 0 }} 份</span>
            </div>
            <div class="detail-row">
              <span class="label">上传进度：</span>
              <el-progress
                :percentage="getUploadProgress(currentExam)"
                :status="getUploadProgress(currentExam) === 100 ? 'success' : undefined"
                style="width: 200px;"
              ></el-progress>
            </div>
          </div>
        </div>

        <!-- 参与考试的学生列表 -->
        <div class="participating-students" v-if="studentList.length > 0">
          <h4>参与考试的学生列表</h4>
          <div class="student-summary">
            <el-tag type="info">共 {{ studentList.length }} 名学生参与考试</el-tag>
            <el-tag type="success" v-if="getUploadedStudentsCount() > 0">已上传 {{ getUploadedStudentsCount() }} 份</el-tag>
            <el-tag type="warning" v-if="studentList.length - getUploadedStudentsCount() > 0">待上传 {{ studentList.length - getUploadedStudentsCount() }} 份</el-tag>
          </div>
          <el-table :data="studentList" border size="mini" max-height="300" style="margin-top: 10px;">
            <el-table-column type="index" label="序号" width="50"></el-table-column>
            <el-table-column prop="studentNumber" label="学号" width="100"></el-table-column>
            <el-table-column prop="studentName" label="姓名" width="80"></el-table-column>
            <el-table-column prop="className" label="班级" width="100"></el-table-column>
            <el-table-column label="上传状态" width="80">
              <template slot-scope="scope">
                <el-tag :type="scope.row.hasUploaded ? 'success' : 'info'" size="mini">
                  {{ scope.row.uploadStatus || (scope.row.hasUploaded ? '已上传' : '未上传') }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="upload-instructions">
          <h4>上传说明</h4>
          <ul>
            <li>支持上传多个试卷文件</li>
            <li>系统将自动识别学生学号和姓名</li>
            <li>识别成功的试卷将自动关联到对应学生</li>
            <li>识别失败的试卷需要手动处理</li>
            <li>请确保文件名包含学生学号或姓名信息</li>
          </ul>
        </div>

        <div class="upload-area">
          <el-upload
            ref="batchUpload"
            :action="batchUploadUrl"
            :headers="uploadHeaders"
            :data="batchUploadData"
            :before-upload="beforeBatchUpload"
            :on-success="handleBatchUploadSuccess"
            :on-error="handleBatchUploadError"
            :on-change="handleBatchFileChange"
            :on-remove="handleBatchFileRemove"
            :file-list="batchFileList"
            :auto-upload="false"
            multiple
            accept="image/*,.pdf"
            drag
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div class="el-upload__tip" slot="tip">
              支持批量上传jpg/png/pdf文件，每个文件不超过10MB
            </div>
          </el-upload>
        </div>

        <div class="upload-progress" v-if="uploadProgress.visible">
          <h4>上传进度</h4>
          <el-progress :percentage="uploadProgress.percentage"></el-progress>
          <p>{{ uploadProgress.text }}</p>
        </div>

        <div class="upload-results" v-if="uploadResults.length > 0">
          <h4>上传结果</h4>
          <el-table :data="uploadResults" border size="mini">
            <el-table-column prop="fileName" label="文件名" min-width="200"></el-table-column>
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 'success' ? 'success' : 'danger'">
                  {{ scope.row.status === 'success' ? '成功' : '失败' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="message" label="说明" min-width="200"></el-table-column>
            <el-table-column prop="studentInfo" label="识别信息" min-width="150"></el-table-column>
          </el-table>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="batchUploadDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="submitBatchUpload" :disabled="batchFileList.length === 0">确定上传</el-button>
      </div>
    </el-dialog>

    <!-- 考试详情对话框 -->
    <el-dialog
      title="考试详情"
      :visible.sync="examDetailDialogVisible"
      width="70%"
    >
      <div class="exam-detail" v-if="currentExam.id">
        <el-row :gutter="20">
          <el-col :span="12">
            <div class="detail-section">
              <h4>基本信息</h4>
              <div class="detail-item">
                <label>考试名称：</label>
                <span>{{ currentExam.examName }}</span>
              </div>
              <div class="detail-item">
                <label>考试描述：</label>
                <span>{{ currentExam.examDescription || '暂无描述' }}</span>
              </div>
              <div class="detail-item">
                <label>考试时长：</label>
                <span>{{ currentExam.examDuration }} 分钟</span>
              </div>
              <div class="detail-item">
                <label>试卷套数：</label>
                <span>{{ currentExam.paperCount || 1 }} 套</span>
              </div>
              <div class="detail-item">
                <label>考试状态：</label>
                <el-tag :type="getStatusTagType(currentExam.status)">
                  {{ getStatusText(currentExam.status) }}
                </el-tag>
              </div>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="detail-section">
              <h4>时间信息</h4>
              <div class="detail-item">
                <label>开始时间：</label>
                <span>{{ formatTime(currentExam.startTime) }}</span>
              </div>
              <div class="detail-item">
                <label>结束时间：</label>
                <span>{{ formatTime(currentExam.endTime) }}</span>
              </div>
              <div class="detail-item">
                <label>创建时间：</label>
                <span>{{ formatTime(currentExam.createTime) }}</span>
              </div>
              <div class="detail-item">
                <label>更新时间：</label>
                <span>{{ formatTime(currentExam.updateTime) }}</span>
              </div>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="20" style="margin-top: 20px;">
          <el-col :span="12">
            <div class="detail-section">
              <h4>参与统计</h4>
              <div class="detail-item">
                <label>参与人数：</label>
                <span>{{ currentExam.totalStudents || 0 }} 人</span>
              </div>
              <div class="detail-item">
                <label>已上传试卷：</label>
                <span>{{ currentExam.uploadedCount || 0 }} 份</span>
              </div>
              <div class="detail-item">
                <label>上传进度：</label>
                <el-progress
                  :percentage="getUploadProgress(currentExam)"
                  :status="getUploadProgress(currentExam) === 100 ? 'success' : undefined"
                ></el-progress>
              </div>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="detail-section">
              <h4>试卷信息</h4>
              <div class="paper-list" v-if="currentExam.papers && currentExam.papers.length > 0">
                <div
                  v-for="paper in currentExam.papers"
                  :key="paper.id"
                  class="paper-item"
                >
                  <el-tag :type="paper.isActive ? 'success' : 'info'" size="small">
                    {{ paper.paperCode }} - {{ paper.paperName }}
                  </el-tag>
                  <span class="paper-status">
                    {{ paper.isActive ? '(当前使用)' : '(备用)' }}
                  </span>
                </div>
              </div>
              <div v-else class="no-papers">
                暂无试卷信息
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="examDetailDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="openExamUpload(currentExam)">上传试卷</el-button>
      </div>
    </el-dialog>

    <!-- 试卷查看对话框 -->
    <el-dialog
      title="查看试卷"
      :visible.sync="viewPaperDialogVisible"
      width="80%"
    >
      <div class="paper-viewer">
        <div class="paper-info">
          <h4>试卷信息</h4>
          <p><strong>学生：</strong>{{ currentStudent.studentName }} ({{ currentStudent.studentNumber }})</p>
          <p><strong>试卷代码：</strong>{{ currentStudent.paperCode }}</p>
          <p><strong>上传时间：</strong>{{ formatTime(currentStudent.uploadTime) }}</p>
          <p><strong>AI识别状态：</strong>{{ getAIRecognitionText(currentStudent.aiRecognitionStatus) }}</p>
          <p><strong>识别置信度：</strong>{{ currentStudent.aiConfidence ? (currentStudent.aiConfidence * 100).toFixed(1) + '%' : '-' }}</p>
        </div>

        <!-- 试卷图片显示区域 -->
        <div class="paper-images" v-if="processedPaperImages.length > 0">
          <div class="images-header">
            <h4><i class="el-icon-picture"></i> 试卷图片 ({{ processedPaperImages.length }}张)</h4>
            <p class="images-tip">点击图片可放大查看，支持多张图片切换</p>
          </div>
          <div class="image-gallery">
            <div
              v-for="(image, index) in processedPaperImages"
              :key="index"
              class="image-item"
              @click="previewImage(image, index)"
            >
              <img :src="image" :alt="`试卷图片 ${index + 1}`" />
              <div class="image-overlay">
                <div class="overlay-content">
                  <i class="el-icon-zoom-in"></i>
                  <span class="image-number">第{{ index + 1 }}张</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-images">
          <i class="el-icon-picture"></i>
          <p>暂无试卷图片</p>
        </div>

        <div class="ai-recognition-result" v-if="currentStudent.aiRecognitionResult">
          <h4>AI识别结果</h4>
          <pre>{{ JSON.stringify(currentStudent.aiRecognitionResult, null, 2) }}</pre>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="viewPaperDialogVisible = false">关闭</el-button>
        <el-button type="warning" @click="reuploadPaper(currentStudent)">重新上传</el-button>
      </div>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog
      title="试卷图片预览"
      :visible.sync="imagePreviewVisible"
      width="80%"
      :before-close="closeImagePreview"
    >
      <div class="image-preview-container">
        <img
          :src="currentPreviewImage"
          :alt="`试卷图片 ${currentImageIndex + 1}`"
          class="preview-image"
        />
      </div>
      <div class="preview-actions">
        <el-button @click="prevImage" :disabled="currentImageIndex === 0">上一张</el-button>
        <span class="image-counter">{{ currentImageIndex + 1 }} / {{ processedPaperImages.length }}</span>
        <el-button @click="nextImage" :disabled="currentImageIndex === processedPaperImages.length - 1">下一张</el-button>
      </div>
    </el-dialog>

    <!-- AI识别验证对话框 -->
    <el-dialog
      title="验证AI识别结果"
      :visible.sync="verifyDialogVisible"
      width="600px"
    >
      <div class="verification">
        <div class="recognition-result">
          <h4>AI识别结果</h4>
          <el-form :model="verificationForm" label-width="100px">
            <el-form-item label="识别学号">
              <el-input v-model="verificationForm.recognizedStudentNumber" disabled></el-input>
            </el-form-item>
            <el-form-item label="识别姓名">
              <el-input v-model="verificationForm.recognizedStudentName" disabled></el-input>
            </el-form-item>
            <el-form-item label="实际学号">
              <el-input v-model="verificationForm.actualStudentNumber"></el-input>
            </el-form-item>
            <el-form-item label="实际姓名">
              <el-input v-model="verificationForm.actualStudentName"></el-input>
            </el-form-item>
            <el-form-item label="识别置信度">
              <el-input :value="(verificationForm.confidence * 100).toFixed(1) + '%'" disabled></el-input>
            </el-form-item>
          </el-form>
        </div>

        <div class="verification-actions">
          <el-button type="success" @click="confirmVerification">确认正确</el-button>
          <el-button type="warning" @click="correctVerification">修正信息</el-button>
          <el-button type="danger" @click="rejectVerification">识别错误</el-button>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="verifyDialogVisible = false">取消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { examApi } from '@/api/exam'
import { getToken } from '@/utils/auth'
import request from '@/utils/request'

export default {
  name: 'ExamUpload',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      studentLoading: false,
      exams: [],
      studentList: [],
      currentExam: {},
      currentStudent: {},
      uploadDialogVisible: false,
      singleUploadDialogVisible: false,
      batchUploadDialogVisible: false,
      viewPaperDialogVisible: false,
      verifyDialogVisible: false,
      examDetailDialogVisible: false,
      // 临时开关：禁用试卷图片预览，避免触发 /api/file/preview 请求
      disablePaperPreview: true,
      fileList: [],
      batchFileList: [],
      uploadResults: [],
      uploadProgress: {
        visible: false,
        percentage: 0,
        text: ''
      },
      verificationForm: {
        recognizedStudentNumber: '',
        recognizedStudentName: '',
        actualStudentNumber: '',
        actualStudentName: '',
        confidence: 0
      },
      // 图片预览相关
      imagePreviewVisible: false,
      currentPreviewImage: '',
      currentImageIndex: 0,
      processedPaperImages: [],
      uploadUrl: process.env.VUE_APP_BASE_API + '/exam/upload-multiple',
      batchUploadUrl: process.env.VUE_APP_BASE_API + '/exam/upload-batch',
      uploadHeaders: {
        'Authorization': 'Bearer ' + getToken()
      }
    }
  },
  computed: {
    uploadData() {
      return {
        examId: this.currentExam.id,
        studentId: this.currentStudent.studentId
      }
    },
    batchUploadData() {
      return {
        examId: this.currentExam.id
      }
    }
  },
  mounted() {
    // 不在mounted时自动加载数据，等待父组件调用
  },
  methods: {
    // 获取已上传学生数量
    getUploadedStudentsCount() {
      if (!this.studentList || !Array.isArray(this.studentList)) {
        return 0
      }
      return this.studentList.filter(student => student && student.hasUploaded).length
    },

    // 加载未归档的考试列表
    async loadExams() {
      this.loading = true
      try {
        console.log('=== 开始加载考试列表 ===')
        console.log('courseId:', this.courseId)
        console.log('courseId类型:', typeof this.courseId)

        if (!this.courseId) {
          console.error('courseId为空，无法加载考试列表')
          this.$message.error('课程ID为空，无法加载考试列表')
          return
        }

        // 先尝试使用新的API
        try {
          console.log('尝试调用新API: getUnarchivedExamsWithActivePaper')
          const response = await examApi.getUnarchivedExamsWithActivePaper(this.courseId)
          console.log('新API响应:', response)
          this.exams = response.data || []
          console.log('使用新API加载的考试列表（包含试卷信息）:', this.exams)
          console.log('考试数量:', this.exams.length)
        } catch (newApiError) {
          console.warn('新API调用失败，回退到原API:', newApiError)
          console.log('错误详情:', newApiError.response?.data)
          console.log('错误状态码:', newApiError.response?.status)

          // 如果新API失败，回退到原来的API
          try {
            console.log('尝试调用原API: getUnarchivedExams')
            const response = await examApi.getUnarchivedExams(this.courseId)
            console.log('原API响应:', response)
            this.exams = response.data || []
            console.log('使用原API加载的考试列表:', this.exams)
            console.log('考试数量:', this.exams.length)
          } catch (originalApiError) {
            console.error('原API也调用失败:', originalApiError)
            throw originalApiError
          }
        }

        // 如果没有考试，不显示错误信息，空状态组件会显示"无考试信息"
        if (!this.exams || this.exams.length === 0) {
          console.log('没有找到考试数据，将显示空状态')
        } else {
          console.log('成功加载到考试数据，数量:', this.exams.length)
        }
      } catch (error) {
        console.error('加载考试列表失败:', error)
        console.log('错误响应:', error.response)
        console.log('错误状态码:', error.response?.status)
        console.log('错误数据:', error.response?.data)

        // 检查是否是404错误（没有考试）还是其他错误
        if (error.response && error.response.status === 404) {
          this.exams = []
          console.log('404错误，设置为空数组')
        } else {
          this.$message.error('加载考试列表失败: ' + (error.message || '未知错误'))
        }
      } finally {
        this.loading = false
        console.log('=== 考试列表加载完成 ===')
      }
    },

    // 加载数据（供父组件调用）
    async loadData() {
      await this.loadExams()
    },

    // 打开考试上传页面
    openExamUpload(exam) {
      this.currentExam = exam
      this.uploadDialogVisible = true
      this.loadStudentList()
    },

    // 加载学生列表
    async loadStudentList() {
      console.log('=== 开始加载学生列表 ===')
      console.log('当前考试ID:', this.currentExam.id)
      console.log('当前考试名称:', this.currentExam.examName)

      this.studentLoading = true
      try {
        const response = await examApi.getExamStudents(this.currentExam.id)
        console.log('学生列表API响应:', response)
        console.log('学生列表数据:', response.data)

        this.studentList = response.data || []
        console.log('设置后的学生列表:', this.studentList)
        console.log('学生列表长度:', this.studentList.length)

        // 如果没有学生，显示友好提示
        if (!this.studentList || this.studentList.length === 0) {
          console.log('没有学生数据，显示提示信息')
          this.$message.info('该考试暂无参与学生')
        } else {
          console.log('成功加载学生列表，学生数量:', this.studentList.length)
          // 打印每个学生的详细信息
          this.studentList.forEach((student, index) => {
            console.log(`学生${index + 1}:`, {
              studentId: student.studentId,
              studentNumber: student.studentNumber,
              studentName: student.studentName,
              className: student.className,
              hasUploaded: student.hasUploaded
            })
          })
        }
      } catch (error) {
        console.error('加载学生列表失败:', error)
        console.error('错误详情:', error.response)

        // 检查是否是404错误（没有学生）还是其他错误
        if (error.response && error.response.status === 404) {
          this.studentList = []
          this.$message.info('该考试暂无参与学生')
        } else {
          this.$message.error('加载学生列表失败')
        }
      } finally {
        this.studentLoading = false
        console.log('=== 学生列表加载完成 ===')
      }
    },

    // 刷新学生列表
    refreshStudentList() {
      this.loadStudentList()
    },

    // 单个考试上传
    uploadSingleExam(exam) {
      this.currentExam = exam
      this.uploadDialogVisible = true
      this.loadStudentList()
    },

    // 查看考试详情
    async viewExamDetails(exam) {
      this.currentExam = exam
      this.examDetailDialogVisible = true

      // 加载考试的详细信息，包括试卷信息
      try {
        const response = await examApi.getExamDetail(exam.id)
        if (response && response.data) {
          this.currentExam = { ...exam, ...response.data }
        }
      } catch (error) {
        console.error('获取考试详情失败:', error)
        // 即使获取详情失败，也显示基本信息
      }
    },

    // 计算上传进度
    getUploadProgress(exam) {
      if (!exam.totalStudents || exam.totalStudents === 0) {
        return 0
      }
      const uploaded = exam.uploadedCount || 0
      return Math.round((uploaded / exam.totalStudents) * 100)
    },

    // 单个学生试卷上传
    uploadSingleStudent(student) {
      this.currentStudent = student
      this.fileList = []
      this.singleUploadDialogVisible = true
    },

    // 重新上传试卷
    reuploadPaper(student) {
      this.currentStudent = student
      this.fileList = []
      this.singleUploadDialogVisible = true
    },

    // 查看试卷
    viewPaper(student) {
      if (this.disablePaperPreview) {
        return
      }
      this.currentStudent = student
      this.processPaperImages(student.paperImageUrl)
      this.viewPaperDialogVisible = true
    },

    // 处理试卷图片URL，支持多张图片
    processPaperImages(imageUrl) {
      console.log('=== 处理试卷图片URL ===')
      console.log('原始imageUrl:', imageUrl)

      this.processedPaperImages = []

      if (!imageUrl) {
        console.log('imageUrl为空，返回空数组')
        return
      }

      // 检查是否是JSON数组格式（多张图片）
      if (imageUrl.startsWith('[') && imageUrl.endsWith(']')) {
        console.log('检测到JSON数组格式')
        try {
          const imageArray = JSON.parse(imageUrl)
          console.log('解析后的图片数组:', imageArray)
          this.processedPaperImages = imageArray.map(url => this.getFullImageUrl(url))
        } catch (e) {
          console.error('解析图片URL数组失败:', e)
          this.processedPaperImages = [this.getFullImageUrl(imageUrl)]
        }
      } else if (imageUrl.includes(',')) {
        console.log('检测到逗号分隔格式')
        // 逗号分隔的多个URL
        const urls = imageUrl.split(',').map(url => url.trim())
        console.log('分割后的URL数组:', urls)
        this.processedPaperImages = urls.map(url => this.getFullImageUrl(url))
      } else {
        console.log('检测到单张图片格式')
        // 单张图片
        this.processedPaperImages = [this.getFullImageUrl(imageUrl)]
      }

      console.log('最终处理结果:', this.processedPaperImages)
      console.log('图片数量:', this.processedPaperImages.length)
    },

    // 获取完整的图片URL
    getFullImageUrl(imageUrl) {
      if (!imageUrl) return ''

      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl
      }

      // 如果是API预览接口，使用前端API基础URL
      if (imageUrl.startsWith('/api/file/preview')) {
        // 移除开头的 /api，因为 VUE_APP_BASE_API 已经包含了 /api
        const pathWithoutApi = imageUrl.substring(4) // 移除 '/api'
        return process.env.VUE_APP_BASE_API + pathWithoutApi
      }

      // 如果是MinIO路径，使用MinIO端口
      if (imageUrl.startsWith('/exam-papers/')) {
        return `http://localhost:9000${imageUrl}`
      }

      // 其他情况使用API端口
      return `http://localhost:8080${imageUrl}`
    },

    // 预览图片
    previewImage(image, index) {
      this.currentPreviewImage = image
      this.currentImageIndex = index
      this.imagePreviewVisible = true
    },

    // 关闭图片预览
    closeImagePreview() {
      this.imagePreviewVisible = false
    },

    // 上一张图片
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--
        this.currentPreviewImage = this.processedPaperImages[this.currentImageIndex]
      }
    },

    // 下一张图片
    nextImage() {
      if (this.currentImageIndex < this.processedPaperImages.length - 1) {
        this.currentImageIndex++
        this.currentPreviewImage = this.processedPaperImages[this.currentImageIndex]
      }
    },

    // 验证AI识别结果
    verifyRecognition(student) {
      this.currentStudent = student
      this.verificationForm = {
        recognizedStudentNumber: student.aiRecognitionResult?.studentNumber || '',
        recognizedStudentName: student.aiRecognitionResult?.studentName || '',
        actualStudentNumber: student.studentNumber,
        actualStudentName: student.studentName,
        confidence: student.aiConfidence || 0
      }
      this.verifyDialogVisible = true
    },

    // 显示批量上传对话框
    showBatchUploadDialog(exam = null) {
      console.log('=== 显示批量上传对话框 ===')
      console.log('传入的考试参数:', exam)

      // 如果传入了考试参数，设置为当前考试
      if (exam) {
        this.currentExam = exam
        console.log('设置当前考试:', this.currentExam)
      }

      // 如果没有当前考试，提示用户先选择考试
      if (!this.currentExam || !this.currentExam.id) {
        console.log('没有当前考试，显示警告')
        this.$message.warning('请先选择考试')
        return
      }

      console.log('开始加载学生列表用于批量上传')
      // 加载参与考试的学生列表
      this.loadStudentList()

      this.batchFileList = []
      this.uploadResults = []
      this.uploadProgress.visible = false
      this.batchUploadDialogVisible = true
      console.log('批量上传对话框已显示')
    },

    // 上传前检查
    beforeUpload(file) {
      const isValidType = ['image/jpeg', 'image/png'].includes(file.type)
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isValidType) {
        this.$message.error('只能上传JPG/PNG格式的图片!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB!')
        return false
      }
      return true
    },

    // 批量上传前检查
    beforeBatchUpload(file) {
      return this.beforeUpload(file)
    },

    // 文件变化处理
    handleFileChange(file, fileList) {
      console.log('文件变化:', file, fileList)
      console.log('文件状态:', file.status)
      console.log('文件原始对象:', file.raw)
      this.fileList = fileList
    },

    // 文件移除处理
    handleFileRemove(file, fileList) {
      console.log('文件移除:', file, fileList)
      this.fileList = fileList
    },

    // 批量文件变化处理
    handleBatchFileChange(file, fileList) {
      console.log('批量文件变化:', file, fileList)
      this.batchFileList = fileList
    },

    // 批量文件移除处理
    handleBatchFileRemove(file, fileList) {
      console.log('批量文件移除:', file, fileList)
      this.batchFileList = fileList
    },

    // 多张图片上传成功
    handleUploadSuccess(response, file, fileList) {
      console.log('=== 多张图片上传成功 ===')
      console.log('响应数据:', response)
      console.log('文件信息:', file)
      console.log('文件列表:', fileList)

      // 修复：后端返回code=1表示成功，不是200
      if (response.code === 1) {
        console.log('上传成功，关闭对话框并刷新学生列表')
        const imageCount = response.data?.imageCount || fileList.length
        this.$message.success(`成功上传 ${imageCount} 张图片`)

        // 更新当前学生的图片URL信息
        if (response.data && response.data.imageUrls) {
          this.currentStudent.paperImageUrl = response.data.imageUrls.join(',')
          console.log('更新当前学生图片URL:', this.currentStudent.paperImageUrl)
        }

        this.singleUploadDialogVisible = false
        this.loadStudentList()
      } else {
        console.log('上传失败，响应码:', response.code, '错误信息:', response.msg)
        this.$message.error(response.msg || '上传失败')
      }
    },

    // 多张图片上传失败
    handleUploadError(error, file, fileList) {
      console.log('=== 多张图片上传失败 ===')
      console.log('错误信息:', error)
      console.log('文件信息:', file)
      console.log('文件列表:', fileList)
      console.log('错误响应:', error.response)
      console.log('错误状态:', error.response ? error.response.status : '无状态码')
      console.log('错误数据:', error.response ? error.response.data : '无数据')

      this.$message.error('上传失败')
    },

    // 批量上传成功
    handleBatchUploadSuccess(response, file, fileList) {
      console.log('=== 批量上传成功 ===')
      console.log('响应数据:', response)
      console.log('文件信息:', file)

      // 修复：后端返回code=1表示成功，不是200
      this.uploadResults.push({
        fileName: file.name,
        status: response.code === 1 ? 'success' : 'error',
        message: response.msg || (response.code === 1 ? '上传成功' : '上传失败'),
        studentInfo: response.data ? `${response.data.studentNumber} - ${response.data.studentName}` : ''
      })
    },

    // 批量上传失败
    handleBatchUploadError(error, file, fileList) {
      this.uploadResults.push({
        fileName: file.name,
        status: 'error',
        message: '上传失败',
        studentInfo: ''
      })
    },

    // 提交单个上传
    async submitUpload() {
      console.log('=== 提交单个上传 ===')
      console.log('fileList:', this.fileList)
      console.log('fileList长度:', this.fileList.length)

      if (this.fileList.length === 0) {
        console.log('没有选择文件，显示警告')
        this.$message.warning('请选择要上传的文件')
        return
      }

      // 手动构建FormData
      const formData = new FormData()
      formData.append('examId', this.currentExam.id)
      formData.append('studentId', this.currentStudent.studentId)

      // 添加所有文件
      this.fileList.forEach((file, index) => {
        console.log(`添加文件${index + 1}:`, file.name, file.raw)
        formData.append('files', file.raw)
      })

      console.log('开始上传，文件数量:', this.fileList.length)

      try {
        const response = await request({
          url: '/exam/upload-multiple',
          method: 'post',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            ...this.uploadHeaders
          }
        })

        console.log('上传响应:', response)
        this.handleUploadSuccess(response, null, this.fileList)
      } catch (error) {
        console.error('上传失败:', error)
        this.handleUploadError(error, null, this.fileList)
      }
    },

    // 提交批量上传
    submitBatchUpload() {
      console.log('=== 提交批量上传 ===')
      console.log('batchFileList:', this.batchFileList)
      console.log('batchFileList长度:', this.batchFileList.length)
      console.log('batchUpload ref:', this.$refs.batchUpload)

      // 检查文件列表
      if (!this.batchFileList || this.batchFileList.length === 0) {
        console.log('没有选择文件，显示警告')
        this.$message.warning('请选择要上传的文件')
        return
      }

      console.log('开始批量上传，文件数量:', this.batchFileList.length)
      this.uploadProgress.visible = true
      this.uploadProgress.percentage = 0
      this.uploadProgress.text = '开始上传...'

      this.$refs.batchUpload.submit()
    },

    // 确认验证
    async confirmVerification() {
      try {
        await examApi.confirmAIRecognition(this.currentStudent.id)
        this.$message.success('验证成功')
        this.verifyDialogVisible = false
        this.loadStudentList()
      } catch (error) {
        this.$message.error('验证失败')
      }
    },

    // 修正验证信息
    async correctVerification() {
      try {
        const data = {
          studentNumber: this.verificationForm.actualStudentNumber,
          studentName: this.verificationForm.actualStudentName
        }
        await examApi.correctAIRecognition(this.currentStudent.id, data)
        this.$message.success('修正成功')
        this.verifyDialogVisible = false
        this.loadStudentList()
      } catch (error) {
        this.$message.error('修正失败')
      }
    },

    // 拒绝验证
    async rejectVerification() {
      try {
        await examApi.rejectAIRecognition(this.currentStudent.id)
        this.$message.success('已标记为识别错误')
        this.verifyDialogVisible = false
        this.loadStudentList()
      } catch (error) {
        this.$message.error('操作失败')
      }
    },

    // 关闭上传对话框
    closeUploadDialog() {
      this.uploadDialogVisible = false
      this.currentExam = {}
      this.studentList = []
    },

    // 获取试卷状态类型
    getPaperStatusType(status) {
      const typeMap = {
        0: 'info',
        1: 'success',
        2: 'warning'
      }
      return typeMap[status] || 'info'
    },

    // 获取试卷状态文本
    getPaperStatusText(status) {
      const textMap = {
        0: '未上传',
        1: '已上传',
        2: '识别中'
      }
      return textMap[status] || '未知'
    },

    // 获取AI识别状态类型
    getAIRecognitionType(status) {
      const typeMap = {
        0: 'info',
        1: 'success',
        2: 'danger'
      }
      return typeMap[status] || 'info'
    },

    // 获取AI识别状态文本
    getAIRecognitionText(status) {
      const textMap = {
        0: '未识别',
        1: '识别成功',
        2: '识别失败'
      }
      return textMap[status] || '未知'
    },

    // 获取状态样式类
    getStatusClass(status) {
      const statusMap = {
        1: 'draft',
        2: 'published',
        3: 'ongoing',
        4: 'finished',
        5: 'archived'
      }
      return statusMap[status] || 'draft'
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '草稿',
        2: '已发布',
        3: '进行中',
        4: '已结束',
        5: '已归档'
      }
      return statusMap[status] || '未知'
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'info', // 草稿 - 灰色
        2: 'success', // 已发布 - 绿色
        3: 'warning', // 进行中 - 橙色
        4: 'danger', // 已结束 - 红色
        5: 'info' // 已归档 - 灰色
      }
      return typeMap[status] || 'info'
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString()
    }
  }
}
</script>

<style scoped>
.exam-upload {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
}

.exam-list {
  min-height: 400px;
}

.exam-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.exam-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.exam-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.exam-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.exam-actions {
  display: flex;
  gap: 5px;
}

.exam-content {
  margin-bottom: 15px;
}

.exam-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 15px;
  line-height: 1.5;
}

.exam-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  color: #909399;
  font-size: 12px;
}

.meta-item i {
  margin-right: 5px;
}

.exam-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.exam-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.exam-status.draft {
  background: #f4f4f5;
  color: #909399;
}

.exam-status.published {
  background: #e1f3d8;
  color: #67c23a;
}

.exam-status.ongoing {
  background: #fdf6ec;
  color: #e6a23c;
}

.exam-status.finished {
  background: #f0f9ff;
  color: #409eff;
}

.exam-status.archived {
  background: #fef0f0;
  color: #f56c6c;
}

.exam-time {
  color: #909399;
  font-size: 12px;
}

.upload-container {
  min-height: 500px;
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.upload-header h3 {
  margin: 0;
  color: #303133;
}

.upload-actions {
  display: flex;
  gap: 10px;
}

.student-list {
  margin-bottom: 20px;
}

.missing-paper {
  color: #f56c6c;
  font-weight: bold;
}

.single-upload {
  padding: 20px 0;
}

.student-info {
  margin-bottom: 30px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.student-info h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.student-info p {
  margin: 5px 0;
  color: #606266;
}

.upload-area {
  margin-bottom: 20px;
}

.batch-upload {
  padding: 20px 0;
}

.upload-instructions {
  margin-bottom: 30px;
  padding: 15px;
  background: #f0f9ff;
  border-radius: 4px;
  border-left: 4px solid #409eff;
}

.upload-instructions h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.upload-instructions ul {
  margin: 0;
  padding-left: 20px;
  color: #606266;
}

.upload-instructions li {
  margin-bottom: 5px;
}

.upload-progress {
  margin-bottom: 30px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.upload-progress h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.upload-progress p {
  margin: 10px 0 0 0;
  color: #606266;
  text-align: center;
}

.upload-results {
  margin-bottom: 20px;
}

.upload-results h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.paper-viewer {
  padding: 20px 0;
}

.paper-info {
  margin-bottom: 30px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.paper-info h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.paper-info p {
  margin: 5px 0;
  color: #606266;
}

.paper-image {
  margin-bottom: 30px;
  text-align: center;
}

.ai-recognition-result {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.ai-recognition-result h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.ai-recognition-result pre {
  background: #fff;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
  font-size: 12px;
  color: #606266;
  overflow-x: auto;
}

.verification {
  padding: 20px 0;
}

.recognition-result {
  margin-bottom: 30px;
}

.recognition-result h4 {
  margin: 0 0 20px 0;
  color: #303133;
}

.verification-actions {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.verification-actions .el-button {
  margin: 0 10px;
}

.dialog-footer {
  text-align: right;
}

/* 考试列表样式优化 */
.exam-name-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.exam-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.exam-name {
  font-weight: 600;
  color: #409EFF;
  cursor: pointer;
  transition: color 0.3s;
}

.exam-name:hover {
  color: #66b1ff;
}

.exam-id {
  font-size: 12px;
  color: #909399;
}

.active-paper-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f0f9ff;
  border-radius: 4px;
  border-left: 3px solid #67c23a;
}

.active-paper-info i {
  color: #67c23a;
  font-size: 14px;
}

.paper-name {
  font-size: 12px;
  color: #606266;
  flex: 1;
}

.no-active-paper {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #fef0f0;
  border-radius: 4px;
  border-left: 3px solid #f56c6c;
}

.no-active-paper i {
  color: #f56c6c;
  font-size: 14px;
}

.no-paper-text {
  font-size: 12px;
  color: #909399;
}

.exam-description {
  color: #606266;
  line-height: 1.4;
}

.student-count,
.uploaded-count {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
}

.time-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.start-time {
  font-weight: 500;
  color: #303133;
}

.end-time {
  font-size: 12px;
  color: #909399;
}

/* 考试详情对话框样式 */
.exam-detail {
  padding: 20px 0;
}

.detail-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.detail-section h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #409EFF;
  padding-bottom: 8px;
}

.detail-item {
  display: flex;
  margin-bottom: 12px;
  align-items: flex-start;
}

.detail-item label {
  min-width: 100px;
  font-weight: 500;
  color: #606266;
  margin-right: 10px;
}

.detail-item span {
  color: #303133;
  flex: 1;
}

.paper-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.paper-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.paper-status {
  font-size: 12px;
  color: #909399;
}

.no-papers {
  color: #909399;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

/* 批量上传对话框样式 */
.exam-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  border-left: 4px solid #409EFF;
}

.exam-info h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.exam-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-row .label {
  min-width: 80px;
  font-weight: 500;
  color: #606266;
}

.detail-row .value {
  color: #303133;
  flex: 1;
}

/* 参与考试学生列表样式 */
.participating-students {
  background: #f0f9ff;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  border-left: 4px solid #67c23a;
}

.participating-students h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.student-summary {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.student-summary .el-tag {
  margin: 0;
}

/* 操作按钮样式 */
.operation-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
}

.operation-buttons .el-button {
  margin: 0;
  padding: 7px 12px;
  font-size: 12px;
  min-width: auto;
}

.operation-buttons .el-button + .el-button {
  margin-left: 0;
}

/* 试卷图片画廊样式 */
.paper-images {
  margin-bottom: 20px;
}

.images-header {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.images-header h4 {
  margin: 0 0 5px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.images-header h4 i {
  color: #409eff;
}

.images-tip {
  margin: 0;
  color: #909399;
  font-size: 12px;
}

.image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.image-item {
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
}

.image-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 12px 0 rgba(64, 158, 255, 0.2);
}

.image-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-item:hover .image-overlay {
  opacity: 1;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.overlay-content i {
  color: white;
  font-size: 24px;
}

.image-number {
  color: white;
  font-size: 12px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.3);
  padding: 2px 8px;
  border-radius: 4px;
}

.empty-images {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-images i {
  font-size: 48px;
  margin-bottom: 15px;
  display: block;
}

/* 图片预览样式 */
.image-preview-container {
  text-align: center;
  margin-bottom: 20px;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}

.preview-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.image-counter {
  font-size: 16px;
  color: #606266;
  font-weight: 500;
}
</style>
